import { useId, type CSSProperties, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { ReportGoalsView } from "@gioia365/contracts";
import { goalPlanOf, type GoalStatus, type GoalStep, type GoalStepId } from "@gioia365/domain/report-goals";
import type { ReportTargets } from "@gioia365/domain/report-kpis";
import type { Locale, MessageKey } from "@gioia365/i18n";
import { formatCount, formatEuroCents, formatNumericDate, italianCountArticle } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { targetNumber } from "./report-format";

// «Obiettivi del mese» del Report (piano M7, Task 6; D9; SEZ-1, SEZ-16; mock-up `goalsSection` del 17/10/2026, come il foglio «Calcolo
// Sales»): il mese di oggi, qualunque sia il periodo scelto sopra. Dall'obiettivo di vendita delle persone scelte (con «Tutto il team»
// quello di tutti, per il Commerciale il suo) si risale con il minimo di ogni percentuale a chiusure, demo, disco e chiamate che servono
// (`goalPlanOf` del dominio, la stessa formula del server); per ogni passo «Serve nel mese», «Fatto dal» primo del mese, «A questo ritmo
// a fine mese» con la parola, e la barra con il segno nero di dove si dovrebbe essere oggi. Senza obiettivo, o senza il valore medio,
// la sezione dice che cosa manca e, a chi ha la sezione delle Impostazioni, offre «Imposta gli obiettivi».
//
// I numeri: il valore in euro come gli importi del Report; «Serve nel mese» esatto con al più un decimale, come il foglio («4,5
// chiusure»); il fatto e il ritmo interi; le chiamate al giorno intere, come il mock-up. La barra e la parola si leggono anche senza
// colore: il numero è scritto e la barra ha la sua frase.

const STEP_KEYS: Readonly<Record<GoalStepId, { label: MessageKey; how: MessageKey }>> = {
  value: { label: "reports.goals.steps.value", how: "reports.goals.how.value" },
  closings: { label: "reports.goals.steps.closings", how: "reports.goals.how.closings" },
  demoHeld: { label: "reports.goals.steps.demoHeld", how: "reports.goals.how.demoHeld" },
  demoDue: { label: "reports.goals.steps.demoDue", how: "reports.goals.how.demoDue" },
  discoHeld: { label: "reports.goals.steps.discoHeld", how: "reports.goals.how.discoHeld" },
  discoDue: { label: "reports.goals.steps.discoDue", how: "reports.goals.how.discoDue" },
  calls: { label: "reports.goals.steps.calls", how: "reports.goals.how.calls" },
};

/** La percentuale di ogni passo che divide quello prima. */
const STEP_RATE: Readonly<Partial<Record<GoalStepId, keyof ReportTargets>>> = {
  demoHeld: "closeRate",
  demoDue: "demoShow",
  discoHeld: "demoOnDisco",
  discoDue: "discoShow",
  calls: "discoOnCalls",
};

const STATUS_WORDS: Readonly<Record<GoalStatus, MessageKey>> = {
  inLine: "reports.goals.status.inLine",
  slightlyBehind: "reports.goals.status.slightlyBehind",
  behind: "reports.goals.status.behind",
};

const neededFormatters = new Map<Locale, Intl.NumberFormat>();

/** Quanto serve, esatto con al più un decimale e le migliaia separate: «4,5», «1.875». */
export function neededText(locale: Locale, value: number): string {
  let formatter = neededFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { useGrouping: "always", maximumFractionDigits: 1 });
    neededFormatters.set(locale, formatter);
  }
  return formatter.format(value);
}

/** Un numero di un passo: i centesimi in euro (interi per il ritmo), gli altri esatti per «Serve», interi per fatto e ritmo. */
function stepNumber(locale: Locale, id: GoalStepId, value: number, kind: "needed" | "done" | "projected"): string {
  if (id === "value") return formatEuroCents(locale, kind === "projected" ? Math.round(value / 100) * 100 : Math.round(value));
  return kind === "needed" ? neededText(locale, value) : formatCount(locale, Math.round(value));
}

/** «del 20%», «dell'8%»: l'articolo italiano viene dal suono del numero («otto», «undici», «ottanta» con la vocale). */
export function ratePhrase(t: Translate, locale: Locale, rate: number): string {
  const article = italianCountArticle(Math.floor(rate));
  const of = article === "i" ? t("reports.goals.of.consonant") : t("reports.goals.of.vowel");
  return `${of}${targetNumber(locale, rate)}%`;
}

/** Una frase del catalogo con i valori in grassetto: i segnaposti diventano `<strong>`, l'ordine delle parole resta della lingua. */
function withStrong(text: (marks: Record<string, string>) => string, values: Record<string, string>): ReactNode {
  const marks = Object.fromEntries(Object.keys(values).map((key, index) => [key, `\u0001${index}\u0001`]));
  const parts = text(marks).split("\u0001");
  const keys = Object.keys(values);
  return parts.map((part, index) => {
    if (index % 2 === 0) return part;
    const key = keys[Number(part)] ?? "";
    return <strong key={key}>{values[key]}</strong>;
  });
}

type Whose = { kind: "own" } | { kind: "team" } | { kind: "chosen"; names: readonly string[] };

type Props = {
  goals: ReportGoalsView;
  targets: ReportTargets;
  whose: Whose;
  /** Se chi guarda ha Impostazioni → «Obiettivi» nel suo menu: allora, quando manca qualcosa, «Imposta gli obiettivi». */
  canSet: boolean;
};

export function GoalsSection({ goals, targets, whose, canSet }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  const from = formatNumericDate("UTC", goals.from);
  const to = formatNumericDate("UTC", goals.to);

  if ("missing" in goals) {
    return (
      <section className="g-report-section g-report-goals" aria-labelledby={titleId}>
        <div className="g-report-head">
          <h2 id={titleId}>{t("reports.goals.title")}</h2>
        </div>
        <div className="g-report-goals__missing">
          <p>{goals.missing === "goal" ? t("reports.goals.missingGoal", { from, to }) : t("reports.goals.missingAverage")}</p>
          {canSet ? (
            <Link to="/impostazioni/obiettivi" className="g-report-goals__set">
              {t("reports.goals.setGoals")}
            </Link>
          ) : null}
        </div>
      </section>
    );
  }

  const plan = goalPlanOf({
    salesGoalCents: goals.salesGoalCents,
    averageSaleCents: goals.averageSaleCents,
    targets,
    done: goals.done,
    workingDays: goals.workingDays,
    elapsedWorkingDays: goals.elapsedWorkingDays,
  });
  // Il giorno lavorativo di oggi nel mese (il mock-up conta i giorni fino a oggi compreso), dal server: quello di oggi anche prima
  // dell'apertura, l'ultimo lavorato in un giorno chiuso; almeno il primo.
  const day = Math.min(goals.workingDays, Math.max(1, goals.workingDaysSoFar));
  const goalText = formatEuroCents(locale, goals.salesGoalCents);
  const goalLine =
    whose.kind === "own"
      ? withStrong((marks) => t("reports.goals.goalOwn", marks), { value: goalText })
      : whose.kind === "team"
        ? withStrong((marks) => t("reports.goals.goalTeam", marks), { value: goalText })
        : whose.names.length === 1
          ? withStrong((marks) => t("reports.goals.goalPerson", { ...marks, name: whose.names[0] ?? "" }), { value: goalText })
          : withStrong((marks) => t("reports.goals.goalPeople", { ...marks, names: whose.names.join(", ") }), { value: goalText });

  return (
    <section className="g-report-section g-report-goals" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <h2 id={titleId}>{t("reports.goals.title")}</h2>
          <p className="g-report-hint">{t("reports.goals.intro", { from, to, day: formatCount(locale, day), days: formatCount(locale, goals.workingDays) })}</p>
        </div>
      </div>
      <p className="g-report-goals__inputs">
        <span>{goalLine}</span>
        <span>{withStrong((marks) => t("reports.goals.averageSale", marks), { value: formatEuroCents(locale, goals.averageSaleCents) })}</span>
        <span>
          {withStrong((marks) => t("reports.goals.callsPerDay", marks), {
            needed: formatCount(locale, Math.round(plan.callsPerDayNeeded)),
            average: formatCount(locale, Math.round(plan.callsPerDayAverage)),
          })}
        </span>
      </p>
      {goals.peopleWithGoal < goals.peopleChosen ? (
        <p className="g-report-hint">{t("reports.goals.partial", { with: formatCount(locale, goals.peopleWithGoal), of: formatCount(locale, goals.peopleChosen) })}</p>
      ) : null}
      <ol className="g-report-funnel">
        {plan.steps.map((step) => (
          <GoalStepItem key={step.id} step={step} from={from} averageSaleCents={goals.averageSaleCents} targets={targets} />
        ))}
      </ol>
      <p className="g-report-footnote">{t("reports.goals.footnote")}</p>
    </section>
  );
}

type StepProps = { step: GoalStep; from: string; averageSaleCents: number; targets: ReportTargets };

function GoalStepItem({ step, from, averageSaleCents, targets }: StepProps) {
  const t = useT();
  const { locale } = useLocale();
  const keys = STEP_KEYS[step.id];
  const rateKey = STEP_RATE[step.id];
  const how =
    step.id === "closings"
      ? t(keys.how, { value: formatEuroCents(locale, averageSaleCents) })
      : rateKey !== undefined
        ? t(keys.how, { rate: ratePhrase(t, locale, targets[rateKey].min) })
        : t(keys.how);
  const label = t(keys.label);
  const needed = stepNumber(locale, step.id, step.needed, "needed");
  const done = stepNumber(locale, step.id, step.done, "done");
  const fill = Math.min(100, Math.max(0, step.doneShare * 100));
  const mark = Math.min(100, Math.max(0, step.expectedShare * 100));
  return (
    <li className="g-report-funnel__item">
      <div className="g-report-funnel__step">
        <strong>{label}</strong>
        <span>{how}</span>
      </div>
      <div className="g-report-progress" role="img" aria-label={t("reports.goals.bar", { label, done, needed, expected: formatCount(locale, Math.round(mark)) })}>
        <div className="g-report-progress__fill" /* design-eccezione: variabile locale con un valore calcolato dai dati */ style={{ "--g-fill": `${fill.toFixed(1)}%` } as CSSProperties} />
        <span className="g-report-progress__mark" /* design-eccezione: variabile locale con un valore calcolato dai dati */ style={{ "--g-mark": `${mark.toFixed(1)}%` } as CSSProperties} />
      </div>
      <div className="g-report-funnel__nums">
        <span>{withStrong((marks) => t("reports.goals.needed", marks), { value: needed })}</span>
        <span>{withStrong((marks) => t("reports.goals.done", { ...marks, day: from }), { value: done })}</span>
        <span>
          {withStrong((marks) => t("reports.goals.projected", marks), { value: stepNumber(locale, step.id, step.projected, "projected") })}
          {t("reports.goals.separator")}
          <span className="g-report-goals__word" data-status={step.status}>
            {t(STATUS_WORDS[step.status])}
          </span>
        </span>
      </div>
    </li>
  );
}
